import { useState } from 'react';
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  IconButton,
  Input,
  SimpleGrid,
  Text,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import { MdAdd, MdDelete, MdEdit, MdCheck, MdClose } from 'react-icons/md';
import Card from 'components/card/Card';
import { useFinance } from 'contexts/FinanceContext';
import { Category, TransactionType } from 'types/finance';

const CATEGORY_COLORS = [
  '#48BB78', '#38B2AC', '#4299E1', '#9F7AEA',
  '#ED8936', '#E53E3E', '#DD6B20', '#D69E2E',
  '#ED64A6', '#38A169', '#805AD5', '#718096',
];

function CategorySection({
  title,
  type,
}: {
  title: string;
  type: TransactionType;
}) {
  const { getCategoriesByType, addCategory, updateCategory, deleteCategory } =
    useFinance();
  const categories = getCategoriesByType(type);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const toast = useToast();

  const handleAdd = () => {
    if (!newName.trim()) return;
    const color =
      CATEGORY_COLORS[categories.length % CATEGORY_COLORS.length];
    addCategory(newName, type, color);
    setNewName('');
    toast({ title: 'Category added', status: 'success', duration: 3000 });
  };

  const handleSaveEdit = (id: string) => {
    if (!editName.trim()) return;
    updateCategory(id, editName);
    setEditingId(null);
    toast({ title: 'Category updated', status: 'success', duration: 3000 });
  };

  const handleDelete = (id: string) => {
    const error = deleteCategory(id);
    if (error) {
      toast({ title: error, status: 'error', duration: 4000 });
      return;
    }
    toast({ title: 'Category deleted', status: 'info', duration: 3000 });
  };

  return (
    <Card p="25px">
      <Text color={textColor} fontSize="xl" fontWeight="700" mb="20px">
        {title}
      </Text>
      <Flex gap="10px" mb="20px">
        <FormControl>
          <FormLabel fontSize="sm">New Category</FormLabel>
          <Input
            size="sm"
            placeholder={`Add ${type} category...`}
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
          />
        </FormControl>
        <Button
          mt="28px"
          size="sm"
          colorScheme="brand"
          leftIcon={<Icon as={MdAdd} />}
          onClick={handleAdd}
        >
          Add
        </Button>
      </Flex>
      {categories.map((cat: Category) => (
        <Flex
          key={cat.id}
          align="center"
          py="10px"
          borderBottom="1px solid"
          borderColor="gray.100"
          gap="10px"
        >
          <Box w="12px" h="12px" borderRadius="full" bg={cat.color} />
          {editingId === cat.id ? (
            <>
              <Input
                size="sm"
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                flex="1"
              />
              <IconButton
                aria-label="Save"
                icon={<Icon as={MdCheck} />}
                size="sm"
                colorScheme="green"
                onClick={() => handleSaveEdit(cat.id)}
              />
              <IconButton
                aria-label="Cancel"
                icon={<Icon as={MdClose} />}
                size="sm"
                onClick={() => setEditingId(null)}
              />
            </>
          ) : (
            <>
              <Text flex="1" color={textColor} fontWeight="500">
                {cat.name}
              </Text>
              <IconButton
                aria-label="Edit"
                icon={<Icon as={MdEdit} />}
                size="sm"
                variant="ghost"
                onClick={() => {
                  setEditingId(cat.id);
                  setEditName(cat.name);
                }}
              />
              <IconButton
                aria-label="Delete"
                icon={<Icon as={MdDelete} />}
                size="sm"
                variant="ghost"
                colorScheme="red"
                onClick={() => handleDelete(cat.id)}
              />
            </>
          )}
        </Flex>
      ))}
    </Card>
  );
}

export default function Categories() {
  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <SimpleGrid columns={{ base: 1, lg: 2 }} gap="20px">
        <CategorySection title="Income Categories" type="income" />
        <CategorySection title="Expense Categories" type="expense" />
      </SimpleGrid>
    </Box>
  );
}
